import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { Hub } from 'aws-amplify'
import { Auth } from 'utils/Auth'
import { NEXT_GEN_APP_URL } from 'utils/AllURLs'

const DEFAULT_CONTEXT = {
  session: null,
}

const AuthenticationObserverContext = React.createContext(DEFAULT_CONTEXT)

export const Consumer = AuthenticationObserverContext.Consumer
const Provider = AuthenticationObserverContext.Provider

export const AuthenticationObserverProvider = ({ children = null }) => {
  const [session, setSession] = useState(null)

  const postMessageResponseCallback = useCallback((event, message) => {
    event.source.postMessage(message, event.origin)
  }, [])

  const sendAuthSessionMessageCallback = useCallback(
    async (event, type) => {
      const session = await Auth.currentSession()
      postMessageResponseCallback(event, {
        type,
        payload: {
          idToken: session.idToken.jwtToken,
          accessToken: session.accessToken.jwtToken,
        },
      })

      setSession(session)
    },
    [postMessageResponseCallback],
  )

  const tokenRefreshedCallback = useCallback(async () => {
    // Store the session in local state
    setSession(await Auth.currentSession())
  }, [])

  /**
   * Effect to listen for token refresh events
   */
  useEffect(() => {
    const cancel = Hub.listen('auth', ({ payload: { event } }) => {
      if (event === 'tokenRefresh') {
        // When the token is refreshed, invoke the callback
        tokenRefreshedCallback()
      }
    })

    return () => {
      cancel()
    }
  }, [tokenRefreshedCallback])

  /**
   * Effect to listen for iframe-ready messages
   */
  useEffect(() => {
    const messageListener = event => {
      if (event.origin !== NEXT_GEN_APP_URL) return
      if (event.data.type === 'iframe-ready') {
        sendAuthSessionMessageCallback(event, 'auth')
      } else if (event.data.type === 'refresh-token-request') {
        sendAuthSessionMessageCallback(event, 'refresh-token')
      }
    }

    window.addEventListener('message', messageListener)

    return () => {
      window.removeEventListener('message', messageListener)
    }
  }, [sendAuthSessionMessageCallback])

  const contextValue = useMemo(() => ({ session }), [session])

  return <Provider value={contextValue}>{children}</Provider>
}

export const useAuthenticationObserverContext = () =>
  useContext(AuthenticationObserverContext)
